// Cliente HTTP de la cuenta de servicio y la auditoría de acciones de IA
// (helpdesk/ai-service-account y helpdesk/ai-actions).
//
// Calca el patrón de aiAgentsApi.js / aiSettingsApi.js: mismo BFF, mismos
// headers Bearer, mismos errores tipados con `.status` para poder distinguir
// "backend aún no desplegado" (isEndpointMissing, re-exportado desde ahí — no
// se duplica) de un error real.
//
// SEGURIDAD: la contraseña de la cuenta de servicio viaja SOLO en el body del
// PUT (nunca en query/URL) y el backend jamás la devuelve — el GET entrega
// únicamente configured/email/status/last_error/token_last4. Prohibido loguear
// payloads acá.

import { isEndpointMissing } from "./aiSettingsApi";

// Typed error shared by every helpdesk client (status, code, capabilities and
// the backend validation lists in err.messages).
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// Re-export: las vistas de acciones importan todo desde este módulo, pero la
// implementación vive en un solo lugar (aiSettingsApi).
export { isEndpointMissing };

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

const SERVICE_ACCOUNT_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/ai-service-account`;
const ACTIONS_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/ai-actions`;

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * Cuenta de servicio del courier: con qué usuario de Beartrack ejecuta la IA
 * las acciones del caso. Contrato:
 * { ok, account: { configured, email, status: 'active'|'revoked'|'invalid',
 *   last_login_at, last_used_at, last_error, token_last4 } } — nunca la
 * contraseña ni el token completo.
 * @returns {Promise<object>}
 */
export async function getAiServiceAccount() {
  const response = await authFetch(SERVICE_ACCOUNT_PATH, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Configura (o reemplaza) la cuenta de servicio. `password` viaja SOLO en este
 * body: el front no la guarda, no la reenvía en un PUT posterior ni la loguea.
 * @param {{email: string, password: string}} payload
 * @returns {Promise<object>} cuenta enmascarada (mismo shape que el GET)
 */
export async function saveAiServiceAccount({ email, password }) {
  const response = await authFetch(SERVICE_ACCOUNT_PATH, {
    method: "PUT",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Revoca la cuenta de servicio: la IA deja de poder ejecutar acciones hasta
 * que se configure una nueva. Las credenciales guardadas se descartan.
 * @returns {Promise<{ok: boolean}>} un 204 sin body también resuelve
 */
export async function deleteAiServiceAccount() {
  const response = await authFetch(SERVICE_ACCOUNT_PATH, {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  // Un DELETE puede responder 204 sin body: parsear JSON ahí tira SyntaxError.
  if (response.status === 204) return { ok: true };
  return response.json().catch(() => ({ ok: true }));
}

/**
 * Prueba de inicio de sesión real (user-triggered, timeout en el backend).
 * Sin body: el backend usa la credencial guardada — el front nunca la reenvía.
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function testAiServiceAccount() {
  const response = await authFetch(`${SERVICE_ACCOUNT_PATH}/test`, {
    method: "POST",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Auditoría de acciones ejecutadas por la IA. Contrato: { ok, actions: [{ id,
 * capability, kind, status, agent_id, conversation_id, args, http_status,
 * error, requested_at, confirmed_at, executed_at, actor }] }.
 *
 * Los filtros vacíos se OMITEN del querystring: mandar `agent_id=` (string
 * vacío) no es lo mismo que no filtrar, y el backend puede leerlo como un id
 * inválido.
 * @param {{days?: number, agentId?: string, conversationId?: string, limit?: number}} [filters]
 * @returns {Promise<object>}
 */
export async function getAiActions(filters = {}) {
  const { days = 7, agentId = "", conversationId = "", limit = 50 } = filters;
  const qs = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (agentId) qs.set("agent_id", agentId);
  if (conversationId) qs.set("conversation_id", conversationId);

  const response = await authFetch(`${ACTIONS_PATH}?${qs}`, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}
